// Shared article-metadata model for the News article events (HB-960). Every
// article surface builds its properties through this one function so reads can
// be broken down and rolled up by artist, topic and content type regardless of
// which collection the article came from.
export enum ArticleContentType {
	// HENI editorial article — /news/article, /news/mobile-article
	HeniArticle = 'heni_article',
	// Aggregated third-party article — /news/view
	ExternalArticle = 'external_article',
	// HENI Art News newsletter issue — /news/newsletter
	Newsletter = 'newsletter',
}

export interface ArticleMetadata {
	content_type: ArticleContentType;
	featured_artists?: string;
	topics?: string;
	publish_date?: string;
}

interface GetArticleMetadataInput {
	contentType: ArticleContentType;
	artists?: (string | null | undefined)[] | null;
	// Broadest topic first, so the head of the list reads as the article's
	// primary topic once the list is split downstream.
	topics?: (string | null | undefined)[] | null;
	publishDate?: string | null;
}

const toTagList = (values?: (string | null | undefined)[] | null) => {
	const named = (values ?? [])
		.map((value) => value?.trim())
		.filter((value): value is string => Boolean(value));

	return [...new Set(named)];
};

// OpenPanel flattens arrays into indexed keys, which cannot be grouped on, so
// dates and tag lists are normalised to plain strings here.
const toIsoDate = (value?: string | null) => {
	if (!value) {
		return undefined;
	}

	const parsed = new Date(value);

	return Number.isNaN(parsed.getTime())
		? undefined
		: parsed.toISOString().slice(0, 10);
};

export const getArticleMetadata = ({
	contentType,
	artists,
	topics,
	publishDate,
}: GetArticleMetadataInput): ArticleMetadata => {
	const artistNames = toTagList(artists);
	const topicNames = toTagList(topics);
	const publishedOn = toIsoDate(publishDate);

	return {
		content_type: contentType,
		// Multi-value tags go out as one comma-joined string per property so
		// nothing is lost; splitting them back out is a downstream concern.
		...(artistNames.length && { featured_artists: artistNames.join(', ') }),
		...(topicNames.length && { topics: topicNames.join(', ') }),
		...(publishedOn && { publish_date: publishedOn }),
	};
};
